// src/admin/components/payments/PaymentDetailView.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  RiMoneyDollarCircleLine, 
  RiArrowLeftLine,
  RiPencilLine,
  RiDeleteBin6Line,
  RiUserLine,
  RiCalendarLine,
  RiFileTextLine,
  RiAlertLine,
  RiHomeSmileLine,
  RiBuildingLine,
  RiPrinterLine,
  RiPhoneLine,
  RiArrowRightLine
} from 'react-icons/ri';
import { getPayment, deletePayment, formatCurrency, formatPaymentDate, getPaymentTypeColor } from '../../../models/paymentModel';
import { useTheme } from '../../../contexts/ThemeContext';
import { getCurrentUser } from '../../../models/authModel';
import PrintReceipt from './PrintReceipt';

const PaymentDetailView = () => {
  const { t, i18n } = useTranslation();
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const { id } = useParams();
  
  // Check if the current language is RTL
  const isRTL = i18n.language === 'ar' || i18n.language === 'ku';
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [payment, setPayment] = useState(null);
  
  // Delete confirmation dialog state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Print receipt state
  const [showReceipt, setShowReceipt] = useState(false);

  // Load payment data
  useEffect(() => {
    const fetchPaymentData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await getPayment(id);
        setPayment(response.payment);
        
      } catch (err) {
        console.error('Error fetching payment data:', err);
        setError(err.message || t('failed_to_load_payment_data') || 'Failed to load payment data');
      } finally {
        setLoading(false);
      }
    };
    
    if (id) {
      fetchPaymentData();
    }
  }, [id, t]);

  // Format payment period for display
  const formatPaymentPeriod = (payment) => {
    if (!payment.months) return t('unknown_period') || 'Unknown period';
    
    try {
      const months = Array.isArray(payment.months) 
        ? payment.months 
        : JSON.parse(payment.months);
      
      if (months.length >= 2) {
        return `${formatPaymentDate(months[0])} ${t('to')} ${formatPaymentDate(months[1])}`;
      }
      
      return months[0] ? formatPaymentDate(months[0]) : t('unknown_period') || 'Unknown period';
    } catch (err) {
      return t('invalid_period_data') || 'Invalid period data';
    }
  };

  // Handle payment deletion
  const handleDeletePayment = async () => {
    try {
      setDeleting(true);
      
      await deletePayment(id);
      
      navigate('/admin/payments');
      
    } catch (err) {
      console.error('Error deleting payment:', err);
      setError(err.message || t('failed_to_delete_payment') || 'Failed to delete payment');
      setShowDeleteDialog(false);
      setDeleting(false);
    }
  };
  
  // Handle print receipt
  const handlePrintReceipt = () => {
    setShowReceipt(true);
  };

  // Select the appropriate arrow icon based on RTL
  const BackArrow = isRTL ? RiArrowRightLine : RiArrowLeftLine;

  if (loading) {
    return (
      <div className={`${isDarkMode ? 'bg-zinc-900 border-gray-700' : 'bg-white border-gray-200'} rounded-xl border shadow-lg p-8 text-center`}>
        <div className="flex flex-col items-center justify-center space-y-4">
          <div className={`inline-block h-12 w-12 animate-spin rounded-full border-4 ${
            isDarkMode ? 'border-green-500 border-t-transparent' : 'border-green-500 border-t-transparent'
          }`}></div>
          <p className={`mt-2 text-lg font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            {t('loading_payment_data') || 'Loading payment data...'}
          </p>
        </div>
      </div>
    );
  }

  if (error || !payment) {
    return (
      <div className={`${isDarkMode ? 'bg-zinc-900 border-gray-700' : 'bg-white border-gray-200'} rounded-xl border shadow-lg p-8 text-center`}>
        <RiAlertLine className={`mx-auto text-6xl mb-4 ${isDarkMode ? 'text-red-400' : 'text-red-500'}`} />
        <h3 className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          {t('error_loading_payment') || 'Error Loading Payment'}
        </h3>
        <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
          {error || t('payment_not_found') || 'Payment not found'}
        </p>
        <button
          onClick={() => navigate('/admin/payments')}
          className={`px-4 py-2 rounded-lg ${
            isDarkMode ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-green-500 text-white hover:bg-green-600'
          }`}
        >
          <BackArrow className={`${isRTL ? 'ml-2' : 'mr-2'} inline`} />
          {t('back_to_payments') || 'Back to Payments'}
        </button>
      </div>
    );
  }

  return (
    <div dir={isRTL ? 'rtl' : 'ltr'} className="space-y-6">
      {/* Delete Confirmation Dialog */}
      {showDeleteDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className={`${isDarkMode ? 'bg-zinc-900 border-gray-700' : 'bg-white border-gray-200'} rounded-xl border shadow-lg max-w-md w-full p-6`}>
            <div className="flex items-center mb-4 text-red-500">
              <RiAlertLine size={24} className={`${isRTL ? 'ml-2' : 'mr-2'}`} />
              <h3 className="text-lg font-semibold">{t('confirm_deletion') || 'Confirm Deletion'}</h3>
            </div>
            
            <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-6`}>
              {t('delete_payment_confirmation', { amount: formatCurrency(payment.amount) }) || 
               `Are you sure you want to delete this ${formatCurrency(payment.amount)} payment? This action cannot be undone.`}
            </p>
            
            <div className={`flex justify-end space-x-3 ${isRTL ? 'space-x-reverse' : ''}`}>
              <button
                onClick={() => setShowDeleteDialog(false)}
                className={`px-4 py-2 rounded-lg ${
                  isDarkMode ? 'bg-zinc-800 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
                disabled={deleting}
              >
                {t('cancel') || 'Cancel'}
              </button>
              
              <button
                onClick={handleDeletePayment}
                className={`px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 flex items-center ${
                  deleting ? 'opacity-70 cursor-not-allowed' : ''
                }`}
                disabled={deleting}
              >
                {deleting ? (
                  <>
                    <div className={`h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin ${isRTL ? 'ml-2' : 'mr-2'}`}></div>
                    {t('deleting') || 'Deleting...'}
                  </>
                ) : (
                  <>
                    <RiDeleteBin6Line className={`${isRTL ? 'ml-2' : 'mr-2'}`} />
                    {t('delete') || 'Delete'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Print Receipt Modal */}
      {showReceipt && (
        <PrintReceipt 
          payment={payment} 
          onClose={() => setShowReceipt(false)} 
          isRTL={isRTL}
        />
      )}

      {/* Header */}
      <div className={`${isDarkMode ? 'bg-zinc-900 border-gray-700' : 'bg-white border-gray-200'} rounded-xl border shadow-lg overflow-hidden`}>
        <div className={`p-6 ${isDarkMode ? 'bg-gray-750 border-b border-gray-700' : 'bg-green-50 border-b border-gray-200'}`}>
          <div className="flex justify-between items-start">
            <div className="flex items-center">
              <div className={`h-16 w-16 rounded-full ${
                payment.paymentType === 'rent' 
                  ? 'bg-gradient-to-br from-red-500 to-red-600' 
                  : payment.paymentType === 'service'
                    ? 'bg-gradient-to-br from-green-500 to-green-600'
                    : 'bg-gradient-to-br from-purple-500 to-purple-600'
              } flex items-center justify-center shadow-lg ${isRTL ? 'ml-4' : 'mr-4'}`}>
                <RiMoneyDollarCircleLine className="text-white text-2xl" />
              </div>
              <div>
                <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {formatCurrency(payment.amount)}
                </h1>
                <div className={`flex items-center mt-2 ${isRTL ? 'space-x-4 space-x-reverse' : 'space-x-4'}`}>
                  <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getPaymentTypeColor(payment.paymentType, isDarkMode)}`}>
                    {t(payment.paymentType) || payment.paymentType || t('unknown_type') || 'Unknown Type'}
                  </span>
                  <div className="flex items-center text-sm text-gray-500">
                    <RiCalendarLine className={`${isRTL ? 'ml-1' : 'mr-1'}`} size={16} />
                    <span>{formatPaymentDate(payment.paymentDate)}</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className={`flex items-center ${isRTL ? 'space-x-3 space-x-reverse' : 'space-x-3'}`}>
              <button
                onClick={handlePrintReceipt}
                className={`px-4 py-2 rounded-lg ${
                  isDarkMode ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-zinc-500 text-white hover:bg-red-600'
                } flex items-center`}
              >
                <RiPrinterLine className={`${isRTL ? 'ml-2' : 'mr-2'}`} size={18} />
                {t('print_receipt') || 'Print Receipt'}
              </button>
              
              <Link
                to={`/admin/payments/${id}/edit`}
                className={`px-4 py-2 rounded-lg ${
                  isDarkMode ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-green-500 text-white hover:bg-green-600'
                } flex items-center`}
              >
                <RiPencilLine className={`${isRTL ? 'ml-2' : 'mr-2'}`} size={18} />
                {t('edit') || 'Edit'}
              </Link>
              
              {getCurrentUser()?.role === 'admin' && (
                <button
                  onClick={() => setShowDeleteDialog(true)}
                  className={`px-4 py-2 rounded-lg ${
                    isDarkMode ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-zinc-500 text-white hover:bg-red-600'
                  } flex items-center`}
                >
                  <RiDeleteBin6Line className={`${isRTL ? 'ml-2' : 'mr-2'}`} size={18} />
                  {t('delete') || 'Delete'}
                </button>
              )}
              
              <button
                onClick={() => navigate('/admin/payments')}
                className={`p-2 rounded-lg ${
                  isDarkMode ? 'bg-zinc-800 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <BackArrow size={20} />
              </button>
            </div>
          </div>
        </div>
        
        {/* Summary Cards */}
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-zinc-800' : 'bg-zinc-50'}`}>
              <div className="flex items-center">
                <RiUserLine className={`text-2xl ${isRTL ? 'ml-3' : 'mr-3'} ${isDarkMode ? 'text-red-400' : 'text-red-500'}`} />
                <div>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{t('tenant') || 'Tenant'}</p>
                  <p className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {payment.tenantName || t('unknown_tenant') || 'Unknown Tenant'}
                  </p>
                </div>
              </div>
            </div>
            
            <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-zinc-800' : 'bg-purple-50'}`}>
              <div className="flex items-center">
                <RiHomeSmileLine className={`text-2xl ${isRTL ? 'ml-3' : 'mr-3'} ${isDarkMode ? 'text-purple-400' : 'text-purple-500'}`} />
                <div>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{t('apartment') || 'Apartment'}</p>
                  <p className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {payment.apartmentNumber ? `${t('apt')} ${payment.apartmentNumber}` : t('none') || 'None'}
                  </p>
                </div>
              </div>
            </div>
            
            <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-zinc-800' : 'bg-green-50'}`}>
              <div className="flex items-center">
                <RiCalendarLine className={`text-2xl ${isRTL ? 'ml-3' : 'mr-3'} ${isDarkMode ? 'text-green-400' : 'text-green-500'}`} />
                <div>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{t('payment_period') || 'Payment Period'}</p>
                  <p className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {formatPaymentPeriod(payment)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Details */}
      <div className={`${isDarkMode ? 'bg-zinc-900 border-gray-700' : 'bg-white border-gray-200'} rounded-xl border shadow-lg overflow-hidden`}>
        <div className={`p-4 ${isDarkMode ? 'border-b border-gray-700' : 'border-b border-gray-200'}`}>
          <h2 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'} flex items-center`}>
            <RiMoneyDollarCircleLine className={`${isRTL ? 'ml-2' : 'mr-2'}`} />
            {t('payment_details') || 'Payment Details'}
          </h2>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Tenant Information */}
            <div className={`p-6 rounded-lg ${isDarkMode ? 'bg-zinc-800' : 'bg-gray-50'}`}>
              <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'} flex items-center`}>
                <RiUserLine className={`${isRTL ? 'ml-2' : 'mr-2'}`} />
                {t('tenant_information') || 'Tenant Information'}
              </h3>
              
              <div className="space-y-3">
                <div className="flex items-center">
                  <RiUserLine className={`${isRTL ? 'ml-3' : 'mr-3'} ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`} size={18} />
                  <span className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {payment.tenantName || t('unknown_tenant') || 'Unknown Tenant'}
                  </span>
                </div>
                
                {payment.tenantPhone && (
                  <div className="flex items-center">
                    <RiPhoneLine className={`${isRTL ? 'ml-3' : 'mr-3'} ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`} size={18} />
                    <span className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      {payment.tenantPhone}
                    </span>
                  </div>
                )}
                
                {payment.apartmentNumber && (
                  <div className="flex items-center">
                    <RiHomeSmileLine className={`${isRTL ? 'ml-3' : 'mr-3'} ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`} size={18} />
                    <span className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      {t('apt')} {payment.apartmentNumber}
                    </span>
                  </div>
                )}
                
                {payment.buildingName && (
                  <div className="flex items-center">
                    <RiBuildingLine className={`${isRTL ? 'ml-3' : 'mr-3'} ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`} size={18} />
                    <span className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      {payment.buildingName}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Payment Information */}
            <div className={`p-6 rounded-lg ${isDarkMode ? 'bg-zinc-800' : 'bg-gray-50'}`}>
              <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'} flex items-center`}>
                <RiMoneyDollarCircleLine className={`${isRTL ? 'ml-2' : 'mr-2'}`} />
                {t('payment_information') || 'Payment Information'}
              </h3>
              
              <div className="space-y-3">
                <div className="flex items-center">
                  <RiMoneyDollarCircleLine className={`${isRTL ? 'ml-3' : 'mr-3'} ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`} size={18} />
                  <span className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {t('amount')}: {formatCurrency(payment.amount)}
                  </span>
                </div>
                
                <div className="flex items-center">
                  <RiCalendarLine className={`${isRTL ? 'ml-3' : 'mr-3'} ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`} size={18} />
                  <span className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {t('payment_date')}: {formatPaymentDate(payment.paymentDate)}
                  </span>
                </div>
                
                <div className="flex items-center">
                  <RiCalendarLine className={`${isRTL ? 'ml-3' : 'mr-3'} ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`} size={18} />
                  <span className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {t('payment_period')}: {formatPaymentPeriod(payment)}
                  </span>
                </div>
                
                <div className="flex items-center">
                  <RiMoneyDollarCircleLine className={`${isRTL ? 'ml-3' : 'mr-3'} ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`} size={18} />
                  <span className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {t('payment_type')}: {t(payment.paymentType) || payment.paymentType || t('unknown') || 'Unknown'}
                  </span>
                </div>

                <div className="flex items-center">
                  <RiCalendarLine className={`${isRTL ? 'ml-3' : 'mr-3'} ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`} size={18} />
                  <span className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {t('created')}: {formatPaymentDate(payment.createdAt)}
                  </span>
                </div>
                
                {payment.updatedAt && payment.updatedAt !== payment.createdAt && (
                  <div className="flex items-center">
                    <RiCalendarLine className={`${isRTL ? 'ml-3' : 'mr-3'} ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`} size={18} />
                    <span className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      {t('last_updated')}: {formatPaymentDate(payment.updatedAt)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Notes */}
          {payment.notes && (
            <div className={`p-6 rounded-lg ${isDarkMode ? 'bg-zinc-800' : 'bg-gray-50'} mt-6`}>
              <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'} flex items-center`}>
                <RiFileTextLine className={`${isRTL ? 'ml-2' : 'mr-2'}`} />
                {t('notes') || 'Notes'}
              </h3>
              <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{payment.notes}</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className={`flex ${isRTL ? 'justify-start' : 'justify-end'} mt-6 ${isRTL ? 'space-x-3 space-x-reverse' : 'space-x-3'}`}>
            <Link
              to={`/admin/tenants/${payment.tenantId}`}
              className={`px-4 py-2 rounded-lg ${
                isDarkMode ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-zinc-500 text-white hover:bg-red-600'
              } flex items-center`}
            >
              <RiUserLine className={`${isRTL ? 'ml-2' : 'mr-2'}`} size={18} />
              {t('view_tenant') || 'View Tenant'}
            </Link>
            
            <Link
              to="/admin/payments"
              className={`px-4 py-2 rounded-lg ${
                isDarkMode ? 'bg-zinc-800 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              } flex items-center`}
            >
              <BackArrow className={`${isRTL ? 'ml-2' : 'mr-2'}`} size={18} />
              {t('back_to_payments') || 'Back to Payments'}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentDetailView;